/** @format */

import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import ModalWrapper from '@components/modals/modal-wrapper';
import { disclaimerData } from './disclaimer-data';

type DisclaimerProps = {
  disclaimerId: keyof typeof disclaimerData;
};

/**
 * Disclaimer Component.
 *
 * @returns JSX.Element | null
 */

const Disclaimer: React.FC<DisclaimerProps> = ({ disclaimerId }) => {
  const { header, text, cookieName, acceptText, declineText, declinePath } =
    disclaimerData[disclaimerId];
  const [showModal, setShowModal] = useState<boolean>(true);
  const [cookies, setCookie] = useCookies([cookieName]);
  const expireDate = new Date();
  expireDate.setDate(expireDate.getDate() + 30);

  /**
   * Accept disclaimer and close modal.
   */
  const acceptDisclaimer = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null
  ) => {
    event?.preventDefault();
    setShowModal(false);
    setCookie(cookieName, true, { path: '/', expires: expireDate });
  };

  /**
   * Decline disclaimer and redirect
   */
  const declineDisclaimer = () => {
    if (declinePath && !cookies[cookieName]) {
      window.location.href = declinePath;
    }
  };

  /**
   *  Reset initial state
   */
  const resetState = () => {
    if (!cookies[cookieName]) {
      setShowModal(true);
    }
  };

  /**
   * Add event listener to call resetState()
   * when navigating forward/back in browser
   */
  useEffect(() => {
    window.addEventListener('pageshow', resetState);
    return () => window.removeEventListener('pageshow', resetState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Listens for modal to close.
   */
  useEffect(() => {
    if (!showModal) {
      declineDisclaimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Render.
   */
  if (!cookies[cookieName]) {
    return (
      <ModalWrapper showModal={showModal} setShowModal={setShowModal}>
        <h2 className="text-align-center font-size-lg">{header}</h2>
        {text}
        <div className={`medium-vertical-margin text-center`}>
          <button
            className="btn-primary"
            type="submit"
            onClick={(e) => acceptDisclaimer(e)}>
            {acceptText}
          </button>
          <span className={'small-horizontal-margin'} />

          {declinePath && (
            <button
              className="btn-secondary"
              type="submit"
              onClick={declineDisclaimer}>
              {declineText}
            </button>
          )}
        </div>
      </ModalWrapper>
    );
  }

  return null;
};
export default Disclaimer;
